import { AppState } from 'react-native';
import { Store } from '../../reducers/store';
import { setShowPrivacyBlur } from '../../reducers/configSlice';
import { isWEB } from '../helpers/getPlatformType';
import {
    PRIVACY_TRIGGER_STATES,
    PRIVACY_HIDE_STATES,
    PRIVACY_SERVICE_STATUS
} from './constants';

/**
 * Privacy Protection Service
 * 
 * Handles task switcher blur and external screen sharing protection
 * by monitoring app state changes and showing/hiding blur overlay
 * when the app goes to background or becomes inactive.
 */
class PrivacyProtectionService {
    constructor() {
        this.appStateSubscription = null;
        this.currentAppState = AppState.currentState;
        this.status = PRIVACY_SERVICE_STATUS.UNINITIALIZED;
    }

    /**
     * Initialize the privacy protection service
     * Sets up AppState listener to monitor background/foreground changes
     */
    initialize() {
        if (this.status === PRIVACY_SERVICE_STATUS.INITIALIZED) {
            return;
        }

        if (isWEB()) {
            console.log('PRIVACY_PROTECTION - Web platform detected, skipping initialization');
            return;
        }

        try {
            this.status = PRIVACY_SERVICE_STATUS.INITIALIZING;

            // Set up AppState listener
            this.appStateSubscription = AppState.addEventListener('change', this.handleAppStateChange.bind(this));

            this.status = PRIVACY_SERVICE_STATUS.INITIALIZED;
        } catch (error) {
            this.status = PRIVACY_SERVICE_STATUS.ERROR;
            console.error('PRIVACY_PROTECTION - Failed to initialize service:', error);
        }
    }

    /**
     * Handle app state changes
     * Shows blur when app goes to background/inactive, hides when active
     * 
     * @param {string} nextAppState - The new app state ('active', 'background', 'inactive')
     */
    handleAppStateChange(nextAppState) {
        try {
            // Show blur when app goes to background or becomes inactive
            if (this.currentAppState === 'active' && PRIVACY_TRIGGER_STATES.includes(nextAppState)) {
                this.showPrivacyBlur();
            }

            // Hide blur when app becomes active
            if (PRIVACY_HIDE_STATES.includes(nextAppState) && PRIVACY_TRIGGER_STATES.includes(this.currentAppState)) {
                this.hidePrivacyBlur();
            }

            this.currentAppState = nextAppState;
        } catch (error) {
            console.error('PRIVACY_PROTECTION - Error handling app state change:', error);
        }
    }

    /**
     * Show the privacy blur overlay
     * Dispatches Redux action to show blur
     */
    showPrivacyBlur() {
        try {
            Store.dispatch(setShowPrivacyBlur(true));
        } catch (error) {
            console.error('PRIVACY_PROTECTION - Error showing privacy blur:', error);
        }
    }

    /**
     * Hide the privacy blur overlay
     * Dispatches Redux action to hide blur
     */
    hidePrivacyBlur() {
        try {
            Store.dispatch(setShowPrivacyBlur(false));
        } catch (error) {
            console.error('PRIVACY_PROTECTION - Error hiding privacy blur:', error);
        }
    }

    /**
     * Manually trigger privacy blur (for testing or special cases)
     */
    triggerPrivacyBlur() {
        this.showPrivacyBlur();
    }

    /**
     * Clean up the service
     * Removes AppState listener and resets state
     */
    cleanup() {
        try {
            if (this.appStateSubscription) {
                this.appStateSubscription.remove();
                this.appStateSubscription = null;
            }
            
            // Hide blur on cleanup
            this.hidePrivacyBlur();

            this.status = PRIVACY_SERVICE_STATUS.UNINITIALIZED;
        } catch (error) {
            console.error('PRIVACY_PROTECTION - Error during cleanup:', error);
        }
    }

    /**
     * Get current service status
     * @returns {string} Current service status
     */
    getServiceStatus() {
        return this.status;
    }

    /**
     * Get current initialization status
     * @returns {boolean} Whether the service is initialized
     */
    getInitializationStatus() {
        return this.status === PRIVACY_SERVICE_STATUS.INITIALIZED;
    }

    /**
     * Get current app state
     * @returns {string} Current app state
     */
    getCurrentAppState() {
        return this.currentAppState;
    }
}

// Create singleton instance
const privacyProtectionService = new PrivacyProtectionService();

// Export service instance and initialization function
export const initializePrivacyProtection = () => {
    privacyProtectionService.initialize();
};

export const cleanupPrivacyProtection = () => {
    privacyProtectionService.cleanup();
};

export const triggerPrivacyBlur = () => {
    privacyProtectionService.triggerPrivacyBlur();
};

export const getPrivacyProtectionStatus = () => {
    return {
        status: privacyProtectionService.getServiceStatus(),
        isInitialized: privacyProtectionService.getInitializationStatus(),
        currentAppState: privacyProtectionService.getCurrentAppState()
    };
};

export default privacyProtectionService;
